import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Start transaction generator on login
  const startSession = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/api/session/start`);
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Could not start transaction generator:', error.message);
      }
    }
  }, []);

  // Stop transaction generator on logout
  const stopSession = useCallback(async () => {
    if (sessionId) {
      try {
        await axios.post(`${API}/api/session/stop?session_id=${sessionId}`);
        setSessionId(null);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Could not stop transaction generator:', error.message);
        }
      }
    }
  }, [sessionId]);

  const login = useCallback(async (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    // Start generating transactions when user logs in
    await startSession();
  }, [startSession]);

  const logout = useCallback(async () => {
    // Stop generating transactions when user logs out
    await stopSession();
    setUser(null);
    setIsAuthenticated(false);
  }, [stopSession]);

  // Handle page close/refresh - stop session
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionId) {
        // Use sendBeacon for reliable cleanup on page close
        navigator.sendBeacon(`${API}/api/session/stop?session_id=${sessionId}`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId]);

  const contextValue = useMemo(() => ({
    user, isAuthenticated, loading, login, logout, sessionId
  }), [user, isAuthenticated, loading, login, logout, sessionId]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
